import React, { useCallback, useMemo } from 'react';
import { Box, capitalizeFirstLetter, Circle, Inscription, ScissorsIcon } from '@common-fe/common-fe';

import regexp from '@/common/regexp';
import { LockboxStatus } from '@/modules/employer/components/Lockbox/lockbox.types';
import useLockboxDetailsStore from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDetails.store';
import { useLockboxDividingModeStore } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDividingMode.store';

import LockboxNavigationButtons from './LockboxNavigationButtons';

const LockboxStatusWithCircle: React.FC<{ lockboxStatus?: LockboxStatus }> = ({ lockboxStatus }) => (
  <Box direction="row" align="center">
    <Inscription data-testid="lockbox-status" margin={{ right: 'spacing8' }}>
      {capitalizeFirstLetter(lockboxStatus?.replace(regexp.DASH_SYMBOL, ' ') || '')}
    </Inscription>
    <Circle data-testid="lockbox-status-circle" status={`${lockboxStatus}_lockbox`} />
  </Box>
);

const LockboxName: React.FC<{ lockboxName?: string }> = ({ lockboxName }) => (
  <Box>
    <Inscription
      data-testid="lockbox-name"
      margin={{ left: 'spacing16' }}
      size="2xl"
      weight={700}
      lineHeight="32px"
      color="textTitle"
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {lockboxName}
    </Inscription>
  </Box>
);

interface Props {
  lockboxId: string;
  lockboxName?: string;
  lockboxStatus?: LockboxStatus;
}

const LockboxDetailsHeader: React.FC<Props> = ({ lockboxId, lockboxName, lockboxStatus }) => {
  const { originalLockbox, isDividingMode, setIsDividingMode } = useLockboxDetailsStore();
  const { numberOfPages } = useLockboxDividingModeStore();

  const hasMultiplePages = useMemo(() => numberOfPages > 1, [numberOfPages]);
  const hasParentLockbox = useMemo(() => !!originalLockbox?.parentId, [originalLockbox?.parentId]);

  const handleSetDiviningMode = useCallback(() => {
    setIsDividingMode(true);
  }, [setIsDividingMode]);

  const actionsList = useMemo(
    () => [
      ...(lockboxStatus === LockboxStatus.NEW && !isDividingMode && hasMultiplePages && !hasParentLockbox
        ? [
            <Box key="dividingMode" onClick={handleSetDiviningMode}>
              <ScissorsIcon color="iconPrimary" />
            </Box>,
          ]
        : []),
    ],
    [lockboxStatus, isDividingMode, hasMultiplePages, hasParentLockbox, handleSetDiviningMode]
  );

  return (
    <Box
      data-testid="lockbox-details-header"
      direction="row"
      align="center"
      justify="between"
      pad={{ vertical: 'spacing8', horizontal: '40px' }}
      border={{ color: 'border2', side: 'bottom', size: 'small' }}
      height={{ min: '48px' }}
    >
      <Box direction="row" align="center">
        <LockboxNavigationButtons lockboxId={lockboxId} />
        <LockboxName lockboxName={lockboxName} />
      </Box>
      <Box pad={{ right: 'spacing8' }} direction="row">
        <LockboxStatusWithCircle lockboxStatus={lockboxStatus} />
        {actionsList?.length ? (
          <Box
            margin={{ horizontal: 'spacing16' }}
            background="border1"
            width={{ min: '1px' }}
            height={{ min: '100%' }}
          />
        ) : null}
        {actionsList}
      </Box>
    </Box>
  );
};

export default LockboxDetailsHeader;
